import { supabase } from '../config/supabase';
import type { Subscription, BillingCycle } from '../types/subscription';

export const getAllSubscriptions = async (): Promise<Subscription[]> => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .order('created_at', { ascending: false });
    
  if (error) {
    console.error('Error fetching subscriptions:', error);
    return [];
  }
  return data.map(mapSubFromDB);
};

export const createSubscription = async (
  sub: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Subscription | null> => {
  const { data: result, error } = await supabase.from('subscriptions').insert({
    service_name: sub.serviceName,
    custom_name: sub.customName,
    logo_key: sub.logoKey,
    category: sub.category,
    amount: sub.amount,
    currency: sub.currency,
    billing_cycle: sub.billingCycle,
    status: sub.status,
    next_billing_date: sub.nextBillingDate,
    started_date: sub.startedDate,
    notes: sub.notes,
  }).select().single();

  if (error) {
    console.error('Error creating subscription:', error);
    return null;
  }
  return mapSubFromDB(result);
};

export const updateSubscription = async (
  id: string,
  updates: Partial<Omit<Subscription, 'id' | 'createdAt'>>
): Promise<Subscription | null> => {
  const dbUpdates: any = {};
  if (updates.serviceName !== undefined) dbUpdates.service_name = updates.serviceName;
  if (updates.customName !== undefined) dbUpdates.custom_name = updates.customName;
  if (updates.logoKey !== undefined) dbUpdates.logo_key = updates.logoKey;
  if (updates.category !== undefined) dbUpdates.category = updates.category;
  if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
  if (updates.currency !== undefined) dbUpdates.currency = updates.currency;
  if (updates.billingCycle !== undefined) dbUpdates.billing_cycle = updates.billingCycle;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.nextBillingDate !== undefined) dbUpdates.next_billing_date = updates.nextBillingDate;
  if (updates.startedDate !== undefined) dbUpdates.started_date = updates.startedDate;
  if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

  const { data, error } = await supabase.from('subscriptions').update(dbUpdates).eq('id', id).select().single();
  
  if (error) {
    console.error('Error updating subscription:', error);
    return null;
  }
  return mapSubFromDB(data);
};

export const deleteSubscription = async (id: string): Promise<boolean> => {
  const { error } = await supabase.from('subscriptions').delete().eq('id', id);
  if (error) {
    console.error('Error deleting subscription:', error);
    return false;
  }
  return true;
};

export const toMonthlyAmount = (amount: number, cycle: BillingCycle): number => {
  switch (cycle) {
    case 'weekly':     return amount * 4.33;
    case 'monthly':    return amount;
    case 'quarterly':  return amount / 3;
    case 'yearly':     return amount / 12;
  }
};

export interface SubscriptionStats {
  activeCount: number;
  trialCount: number;
  monthlyTotal: number;
  yearlyTotal: number;
  categoryBreakdown: Record<string, number>;
}

export const getSubscriptionStats = (subscriptions: Subscription[]): SubscriptionStats => {
  const active = subscriptions.filter(
    (s) => s.status === 'active' || s.status === 'trial'
  );

  let monthlyTotal = 0;
  const categoryBreakdown: Record<string, number> = {};

  for (const sub of active) {
    const monthly = toMonthlyAmount(sub.amount, sub.billingCycle);
    monthlyTotal += monthly;
    categoryBreakdown[sub.category] = (categoryBreakdown[sub.category] || 0) + monthly;
  }

  return {
    activeCount: active.filter((s) => s.status === 'active').length,
    trialCount: active.filter((s) => s.status === 'trial').length,
    monthlyTotal,
    yearlyTotal: monthlyTotal * 12,
    categoryBreakdown,
  };
};

const mapSubFromDB = (row: any): Subscription => ({
  id: row.id,
  serviceName: row.service_name,
  customName: row.custom_name,
  logoKey: row.logo_key,
  category: row.category,
  amount: Number(row.amount),
  currency: row.currency,
  billingCycle: row.billing_cycle,
  status: row.status,
  nextBillingDate: row.next_billing_date,
  startedDate: row.started_date,
  notes: row.notes,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});
